import { useEffect, useRef } from 'react';

/**
 * Fires the matching handler when the user types one of the secret words (matched against the most
 * recent keystrokes). Typing inside inputs/textareas/selects/contenteditable is ignored, so normal
 * data entry never triggers an egg.
 *
 * Every single-character key is recorded — letters, digits, spaces and punctuation alike (only
 * multi-character keys like Enter/Shift/Arrows are skipped). Matching is case-insensitive, so a code
 * may contain spaces or symbols: e.g. `'winter is coming'` relies on the space being captured.
 *
 * The listener registers once; handlers are read from a ref so an inline `codes` object stays fresh
 * without re-binding on every render.
 */
export function useSecretCode(codes: Record<string, () => void>, maxLen = 24) {
    const codesRef = useRef(codes);
    codesRef.current = codes;
    const buffer = useRef('');

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
            if (e.key.length !== 1) return;

            buffer.current = (buffer.current + e.key.toLowerCase()).slice(-maxLen);
            for (const [word, fn] of Object.entries(codesRef.current)) {
                if (buffer.current.endsWith(word.toLowerCase())) {
                    buffer.current = '';
                    fn();
                    break;
                }
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [maxLen]);
}

const KONAMI = [
    'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
    'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a',
];

/** Fires once the classic Konami code (↑↑↓↓←→←→BA) is entered. Separate from useSecretCode because it
 *  matches named arrow keys, not typed characters. */
export function useKonami(onMatch: () => void) {
    const cbRef = useRef(onMatch);
    cbRef.current = onMatch;
    const pos = useRef(0);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            if (key === KONAMI[pos.current]) {
                pos.current += 1;
                if (pos.current === KONAMI.length) {
                    pos.current = 0;
                    cbRef.current();
                }
            } else {
                // Reset — but let the wrong key still count as a fresh first step if it's an up arrow.
                pos.current = key === KONAMI[0] ? 1 : 0;
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
}
